import _ from 'underscore';
import React from 'react';
import { isInternalUrl } from '@bingads-webui/url-util';
import { AjaxStub, AjaxEventHandler, ErrorStub } from '@bingads-webui/instrumentation-stubs';
import { isTestEnvironment } from '@bingads-webui-campaign/global-app-init';
import {
  getInstrumentation as getInstrumentationInstance,
  InstrumentationSchema
} from '@bingads-webui-campaign/instrumentation-setup';
import {
  OdataErrorMapper,
  CampaignApiErrorMapper,
  UrlErrorMapper,
  odataErrorCodes,
  campaignApiErrorCodes,
  invalidDomains,
} from '@bingads-webui-campaign/instrumentation';

import { createErrorMessage } from '@bingads-webui-campaign/error-utilities';

import { defaultSchema, globalErrorSchema } from './instrumentation-schema';
import { DefaultGlobalErrorView } from './global-error-view';

const errorMappers = {
  odataErrorMapper: new OdataErrorMapper(odataErrorCodes),
  campaignApiErrorMapper: new CampaignApiErrorMapper(campaignApiErrorCodes),
  urlErrorMapper: new UrlErrorMapper(invalidDomains),
};

const userErrorCodes = [
  'LOCATIONNOTEXIST',
  'LOCATIONNOTSUPPORTED',
  'SMARTPAGEINVALIDSUBDOMAIN',
  'SMARTPAGESUBDOMAINISNOTAVAILABLE',
  'SMARTPAGECONCURRENTUPDATINGISNOTALLOWED',
  'SMARTPAGEURLISREJECTEDBYEDITORIAL',
  'SMARTPAGEINVALIDSTATUS',
];

const formattedUserErrorCodes = _.chain(userErrorCodes)
  .map(errorCode => errorCode.trim().toUpperCase())
  .value();

const formatNameValue = (name, value) => {
  if (_.isEmpty(value)) {
    return `${name}`;
  } else if (_.isObject(value)) {
    return `${name}|${JSON.stringify(value)}|NetworkIsOnline:${window.navigator.onLine}`;
  }
  return `${name}|${value}`;
};

function stub(instrumentation, serverContext) {
  const stubOptions = {
    pageTrackingId: serverContext && serverContext.PageTrackingId,
    lcid: serverContext && serverContext.CurrentUser && serverContext.CurrentUser.Locale,
  };

  const shouldAddHeaders = ajaxContext => isInternalUrl(ajaxContext.url);
  const eventHandler = new AjaxEventHandler({
    errorMappers,
    shouldAddHeaders,
  });

  const ajaxStub = new AjaxStub({
    stubOptions,
    eventHandler,
    instrumentation,
  });

  const errorStub = new ErrorStub({
    instrumentation,
  });

  ajaxStub.init();
  errorStub.init();
}

export const getInstrumentation = ({ serverContext, isProdRuntime }) => {
  const {
    CurrentCustomer,
    CurrentAccount,
    CurrentUser,
  } = serverContext;

  const userContext = {
    CurrentCustomer: CurrentCustomer || {},
    CurrentAccount: CurrentAccount || {},
    CurrentUser: CurrentUser || {},
  };

  return getInstrumentationInstance({
    pageTrackingId: serverContext.PageTrackingId,
    errorMappers,
    isInDevelopmentMode: isTestEnvironment(document.domain) && !isProdRuntime,
    userContext,
    applicationName: {
      systemName: 'campaign',
      subSystemName: 'smartpage',
    },
    serverContext: {
      ...serverContext,
      ...userContext,
    },
    objectsToStub: [],
  });
};

const isUserErrorCodes = (errorResponseJson) => {
  if (_.isEmpty(errorResponseJson) || !_.isObject(errorResponseJson) ||
    _.isEmpty(errorResponseJson.errors) || !_.isArray(errorResponseJson.errors)) {
    return false;
  }
  const errorCodesInResponse = _.chain(errorResponseJson.errors)
    .pluck('Code')
    .compact()
    .map(code => code.trim().toUpperCase())
    .uniq()
    .value();

  if (_.isEmpty(errorCodesInResponse)) {
    return false;
  }

  for (let index = 0; index < errorCodesInResponse.length; index += 1) {
    if (!_.contains(formattedUserErrorCodes, errorCodesInResponse[index])) {
      return false;
    }
  }
  return true;
};

const isIgnorableError = (errorResponseJson) => {
  if (_.isEmpty(errorResponseJson) || !_.isObject(errorResponseJson)) {
    return false;
  }

  return errorResponseJson.readyState === 0;
};

export const getInstrumentationService = ({ instrumentation, serverContext, scenario = 'defaultScenario' }) => {
  instrumentation.addScenario({
    scenario: [
      ...defaultSchema.scenario,
      ...InstrumentationSchema.scenario,
    ],
  });

  const defaultScenario = instrumentation[scenario].create();
  const logError = defaultScenario.logError.create();
  const logTrace = defaultScenario.logTrace.create();
  const logMetrics = defaultScenario.logMetrics.create();

  stub(instrumentation, serverContext);

  return {
    instrumentation,
    logError: (name, value = '') => {
      const isUserError = isUserErrorCodes(value);
      const isIgnorable = isIgnorableError(value);

      if (!isUserError && !isIgnorable) {
        logError.error(formatNameValue(name, value));
      }
    },
    logTrace: (name, value = '') => {
      logTrace.trace(formatNameValue(name, value));
    },
    logMetrics: (name, value = '') => {
      logMetrics.trace(formatNameValue(name, value));
    },
  };
};

export const getGlobalErrorOptions = ({ instrumentation }) => ({
  logError: ({ error, info }) => {
    if (instrumentation) {
      instrumentation.addScenario(globalErrorSchema);
      const appReactGlobalError = instrumentation.appReactGlobalError.create();
      const logError = appReactGlobalError.logError.create();
      logError.error(createErrorMessage(error));
      logError.error(info.componentStack);
    }
  },
  renderErrorView: () => <DefaultGlobalErrorView />,
});
